/**
 * <s-favorites-list> — renders the favorites saved by <s-favorite-button> on the favorites page.
 * Each card is server-rendered by the product "favorite-card" alternate template, so markup,
 * images and prices stay theme-owned.
 *
 * Attributes:
 *   data-products-base — products path prefix from Liquid routes (e.g. "/products/")
 * Children:
 *   [data-favorites-grid]    — receives the fetched cards
 *   [data-favorites-empty]   — shown when there are no favorites
 *   [data-favorites-loading] — shown while cards load
 */
const STORAGE_KEY = 'yeezo:favorites';

function readFavorites() {
  try {
    const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    return Array.isArray(parsed) ? parsed.filter((item) => typeof item === 'string') : [];
  } catch {
    return [];
  }
}

class SFavoritesList extends HTMLElement {
  #ac;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    this.grid = this.querySelector('[data-favorites-grid]');
    this.empty = this.querySelector('[data-favorites-empty]');
    this.loading = this.querySelector('[data-favorites-loading]');
    document.addEventListener('favorites:change', () => this.#prune(), opts);
    window.addEventListener('storage', (event) => {
      if (event.key === STORAGE_KEY) this.#load();
    }, opts);
    this.#load();
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  async #load() {
    if (!this.grid) return;
    const handles = readFavorites();
    this.grid.replaceChildren();
    if (handles.length === 0) {
      this.#setState('empty');
      return;
    }
    this.#setState('loading');
    const base = this.dataset.productsBase || '';
    const results = await Promise.all(
      handles.map(async (handle) => {
        try {
          const response = await fetch(`${base}${encodeURIComponent(handle)}?view=favorite-card`, {
            signal: this.#ac.signal,
          });
          if (!response.ok) return null;
          const template = document.createElement('template');
          template.innerHTML = (await response.text()).trim();
          return template.content.querySelector('[data-favorite-handle]');
        } catch {
          return null;
        }
      }),
    );
    const cards = results.filter(Boolean);
    this.grid.append(...cards);
    this.#setState(cards.length ? 'list' : 'empty');
  }

  #prune() {
    if (!this.grid) return;
    const handles = readFavorites();
    this.grid.querySelectorAll('[data-favorite-handle]').forEach((card) => {
      if (!handles.includes(card.dataset.favoriteHandle)) card.remove();
    });
    if (!this.grid.querySelector('[data-favorite-handle]')) this.#setState('empty');
  }

  #setState(state) {
    if (this.empty) this.empty.hidden = state !== 'empty';
    if (this.loading) this.loading.hidden = state !== 'loading';
    this.grid.hidden = state !== 'list';
  }
}

if (!customElements.get('s-favorites-list')) customElements.define('s-favorites-list', SFavoritesList);
